import { useField } from '@unform/core'
import _useTranslation from 'lib/hooks/_useTranslation'
import { FunctionComponent, useState, useRef, useEffect } from 'react'
import { Subtitle1 } from 'stories/elements/Typography/Text'
import Search from '../Search'
import { FormGroup, ErrorMessage } from './Input.css'
import { InputProps, InputPropTypes } from './InputTypes'
import theme from 'stories/utils/theme'
import styled from 'styled-components'
import SelectArrowIcon from 'assets/icons/select-arrow.svg'

interface SelectOption {
    label: string
    // eslint-disable-next-line
    data: any
}

const SelectArrow = styled(SelectArrowIcon)`
    position: absolute;
    right: 10px;
    top: 45%;
`

const SelectMultipleWrapper = styled.div<{ isDarkTheme?: boolean }>`
    position: relative;

    & .fake-input {
        min-height: 60px;
        user-select: none;
        padding: 20px;
        p {
            color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.white : theme.colors.placeholderText)};
            margin-bottom: 0px;
            padding: 0;
        }
        .placeholder {
            color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.caption)};
            font-size: 18px;
        }
    }

    & .dropdown {
        position: absolute;
        top: 45px;
        width: 100%;
        background: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.webappDialogBackground : theme.colors.violet)};
        box-shadow: rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px;
        visibility: hidden;
        max-height: 250px;
        overflow-y: scroll;
        z-index: 1;
    }

    & .dropdown.visible {
        z-index: ${theme.zIndex.modal};
        visibility: visible;
    }

    & .option {
        display: grid;
        grid-template-columns: 16px 1fr;
        place-items: center start;
        padding-left: 10px;
        color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.white : theme.colors.body1Black)};
    }

    & .option:hover {
        background: ${({ isDarkTheme }) =>
            isDarkTheme ? theme.colors.backgroundTransparent : theme.colors.violetBackground};
    }

    & .option label {
        width: 100%;
        padding: 10px;
        cursor: pointer;
        user-select: none;
    }
`

const SelectMultiple: FunctionComponent<InputProps> = (props) => {
    const [searchQuery, setSearchQuery] = useState('')
    const { t: tCommon } = _useTranslation('common')
    const { name, options } = props
    const dropdownRef = useRef<HTMLDivElement>(null)
    const fakeInputRef = useRef<HTMLDivElement>(null)
    const { fieldName, defaultValue, registerField, error } = useField(name)
    const [selectedValues, setSelectedValues] = useState<SelectOption[]>([])
    const [showDropdown, setShowDropdown] = useState(false)
    const isError = !!error
    useEffect(() => {
        registerField({
            name: fieldName,
            ref: undefined,
            path: undefined,
            getValue: () => {
                return selectedValues.map((values) => values.data)
            },
            setValue: (ref: HTMLSelectElement, value: SelectOption[]) => {
                setValue(value)
            },
        })
    }, [fieldName, registerField, options, selectedValues])

    useEffect(() => {
        if (defaultValue) {
            setValue(defaultValue)
        }
    }, [defaultValue])

    const setValue = (value: SelectOption[]) => {
        setSelectedValues(value)

        const inputElements = dropdownRef.current?.getElementsByTagName('input')
        Array.from(inputElements!).forEach((inputElement) => {
            value.forEach((option) => {
                if (option.label === inputElement.id) {
                    inputElement.checked = true
                }
            })
        })
    }

    const toggleShowDropdown = () => {
        setShowDropdown(!showDropdown)
    }

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, option: SelectOption) => {
        const isChecked = event.target.checked
        props.onUpdate && props.onUpdate(option.data)
        if (isChecked) {
            setSelectedValues([...selectedValues, option])
        } else {
            setSelectedValues(selectedValues.filter((value) => value.label !== option.label))
        }
    }

    const selectedValuesSorted = selectedValues.sort((a, b) => {
        return options!.indexOf(a) < options!.indexOf(b) ? -1 : 1
    })
    const selectedPreview = selectedValuesSorted.length ? (
        selectedValues.map((value) => value.label).join(', ')
    ) : (
        <span className="placeholder">{props.placeholder}</span>
    )

    useEffect(() => {
        document.addEventListener('mousedown', handleMouseDown)
        return () => {
            document.removeEventListener('mousedown', handleMouseDown)
        }
    }, [])

    const handleMouseDown = (event: MouseEvent) => {
        if (!event.target) return

        const isClickOutside = !dropdownRef.current?.contains(event.target as Element)
        const isClickOnFakeInput = fakeInputRef.current?.contains(event.target as Element)
        if (isClickOutside && !isClickOnFakeInput) {
            setShowDropdown(false)
        }
    }

    const onSubmit = (query: string) => setSearchQuery(query)

    const onSearchDebounce = (query: string) => setSearchQuery(query)

    const filterOptions = (options: SelectOption[]) =>
        options?.filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase()))

    if (!options) return <></>

    return (
        <FormGroup noMargin={!!props.noMargin} isError={isError} darkTheme={props.isDarkTheme}>
            <label>
                <Subtitle1>{props.label ?? props.placeholder}</Subtitle1>
            </label>
            <SelectMultipleWrapper isDarkTheme={props.isDarkTheme}>
                <SelectArrow />
                <div ref={fakeInputRef} className="fake-input" tabIndex={0} onClick={toggleShowDropdown}>
                    <p>{selectedPreview}</p>
                </div>
                <div
                    ref={dropdownRef}
                    className={`dropdown ${showDropdown ? 'visible' : ''}`}
                    data-id={name}
                    data-cy={props.dataCy}
                >
                    <Search
                        onSearch={onSubmit}
                        buttonText={tCommon('common/searchButtonLabel')}
                        onDebounce={onSearchDebounce}
                        placeholder={`${tCommon('common/searchByTitleLabel')}...`}
                        isSmall
                        hideButton
                    />
                    {filterOptions(options).map((option) => (
                        <div className="option" tabIndex={0} key={option.label}>
                            <input
                                type="checkbox"
                                id={option.label}
                                checked={selectedValues.some((value) => value.label === option.label)}
                                onChange={(e) => handleCheckboxChange(e, option)}
                            />
                            <label htmlFor={option.label}>{option.label}</label>
                        </div>
                    ))}
                </div>
            </SelectMultipleWrapper>

            <ErrorMessage>{error}</ErrorMessage>
        </FormGroup>
    )
}

SelectMultiple.propTypes = InputPropTypes

export default SelectMultiple
